import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useCountry } from "../contexts/CountryContext";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import { track } from "../lib/analytics";
import ProductCardV2 from "../components/shop/ProductCardV2";
import TrustStrip from "../components/shop/TrustStrip";
import ComparisonTable from "../components/shop/ComparisonTable";
import StickyWhatsAppButton from "../components/shop/StickyWhatsAppButton";
import CountryPill from "../components/shop/CountryPill";
import PersonalisedPicks from "../components/shop/PersonalisedPicks";
import TrustBadgeRow from "../components/TrustBadgeRow";
import { Button } from "../components/ui/button";
import {
  Sparkles,
  ArrowRight,
  HeartHandshake,
  ShieldCheck,
  Compass,
  Leaf,
  Loader2,
} from "lucide-react";

/**
 * /shop — public storefront landing.
 *
 * Tone: a guided wellness concierge, not a supplement store. Hero
 * orients the visitor, then several calm "paths" are offered before
 * the product grid even begins:
 *
 *   1. Take the free assessment
 *   2. Browse "where do I start" sections
 *   3. Read the comparison table
 *
 * Country-aware: pulls /api/v2/products?country=XX and renders the
 * resolved variants. If a product has no variant in the active country
 * it's silently hidden.
 */
export default function ShopPage() {
  const ctx = useCountry();
  const country = ctx?.country || "UK";
  const currency = ctx?.currency || "GBP";

  const [products, setProducts] = useState(null);
  const [error, setError] = useState(null);

  useSeo({
    title: "Shop — Guided wellness, gently chosen | Feel Greats",
    description:
      "Browse our curated wellness range. Personalised guidance, country-aware pricing, embedded fact sheets and real coaches — choose with calm confidence.",
    canonical: `${PROD_DOMAIN}/shop`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Feel Greats Wellness Shop",
        "url": `${PROD_DOMAIN}/shop`,
        "description":
          "Country-aware, guided wellness commerce. Personalised recommendations, embedded fact sheets, real wellness mentors.",
      },
    ],
  });

  // Fetch when country resolves / changes
  useEffect(() => {
    if (!ctx?.hydrated) return;
    let cancelled = false;
    setProducts(null);
    setError(null);
    api
      .get(`/v2/products?country=${encodeURIComponent(country)}`)
      .then(({ data }) => {
        if (!cancelled) setProducts(data?.items || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load products");
      });
    return () => { cancelled = true; };
  }, [country, ctx?.hydrated]);

  useEffect(() => {
    try { track?.event?.("shop_page_view", { country, currency }); } catch { /* ignore */ }
  }, [country, currency]);

  const featured = useMemo(() => (products || []).slice(0, 1)[0] || null, [products]);
  const grid = useMemo(() => (products || []).slice(1, 7), [products]);
  const rest = useMemo(() => (products || []).slice(7), [products]);

  return (
    <main className="bg-gradient-to-b from-emerald-50/30 via-white to-white overflow-x-hidden" data-testid="shop-page">
      {/* ───────────────── HERO ───────────────── */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 sm:pt-20 sm:pb-16">
          <div className="flex items-center justify-between gap-4 mb-6">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-700 bg-emerald-50/60 rounded-full px-3 py-1 border border-emerald-100">
              <Leaf className="w-3 h-3" /> Guided wellness commerce
            </span>
            <CountryPill variant="full" />
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-emerald-950 leading-[1.05] max-w-3xl"
            data-testid="shop-hero-headline"
          >
            Choose <em className="italic text-emerald-700">slowly</em>.<br />
            Trust the <span className="gold-accent font-medium">guidance</span>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 text-lg text-emerald-900/75 max-w-xl leading-relaxed"
          >
            We're not a supplement shop. We're the layer that helps you choose what's right — and only what's right — for the body you have now.
          </motion.p>

          {/* Two calm entry CTAs */}
          <div className="mt-7 flex flex-wrap gap-3" data-testid="shop-hero-ctas">
            <Link to="/questionnaire" data-testid="shop-hero-assessment-cta">
              <Button className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 py-6 text-base">
                <Sparkles className="w-4 h-4 mr-1.5" /> Take the free assessment first
              </Button>
            </Link>
            <a href="#shop-products" data-testid="shop-hero-browse-cta">
              <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 px-7 py-6 text-base">
                Browse the range <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </a>
          </div>

          <div className="mt-9 max-w-2xl">
            <TrustBadgeRow compact />
          </div>
        </div>
      </section>

      {/* ───────────────── PERSONALISED PICKS (signed-in + assessment) ───────────────── */}
      <PersonalisedPicks />

      {/* ───────────────── PEOPLE-LIKE-YOU NAVIGATION ───────────────── */}
      <PeopleLikeYouRail />

      {/* ───────────────── PRODUCT GRID ───────────────── */}
      <section id="shop-products" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="flex items-end justify-between gap-3 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Our wellness range</p>
            <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light mt-1 leading-tight">
              Curated, never crowded.
            </h2>
            <p className="text-sm text-emerald-900/65 mt-2 max-w-xl leading-relaxed">
              Pricing is in <strong>{currency}</strong> — you can switch country any time.
            </p>
          </div>
        </div>

        {products === null ? (
          <div className="py-20 text-center text-emerald-700/70" data-testid="shop-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            <p className="text-xs uppercase tracking-wider mt-3">Loading the range…</p>
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-amber-200 bg-amber-50/60 p-8 text-center" data-testid="shop-error">
            <p className="text-emerald-900">{error}</p>
          </div>
        ) : products.length === 0 ? (
          <EmptyState country={country} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="shop-product-grid">
              {featured && <ProductCardV2 product={featured} variant="featured" />}
              {grid.map((p) => (
                <ProductCardV2 key={p.id} product={p} />
              ))}
            </div>
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-5">
                {rest.map((p) => <ProductCardV2 key={p.id} product={p} />)}
              </div>
            )}
          </>
        )}
      </section>

      {/* ───────────────── COMPARISON TABLE ───────────────── */}
      {products && products.length > 1 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-12">
          <ComparisonTable products={products} />
        </section>
      )}

      {/* ───────────────── NEED HELP CHOOSING ───────────────── */}
      <NeedHelpChoosingBlock />

      {/* ───────────────── TRUST STRIP ───────────────── */}
      <TrustStrip />

      {/* ───────────────── Sticky concierge button ───────────────── */}
      <StickyWhatsAppButton message="Hi! I'd like guidance on which wellness product would suit me best." />
    </main>
  );
}


// ============================================================================
//   "People like you usually start here" — themed entry-rail
// ============================================================================
const RAILS = [
  {
    key: "energy",
    label: "Low energy or burnout",
    body: "Start gently. Begin with morning calm-energy support and balance from there.",
    href: "/shop?signal=energy",
  },
  {
    key: "weight",
    label: "Slow metabolism, belly fat",
    body: "Most people in this place start with the Feel Great System — small habit, real results.",
    href: "/shop?signal=weight",
  },
  {
    key: "hormone",
    label: "PCOS, peri/menopause",
    body: "Hormonal support that respects your cycle — a calm path forward.",
    href: "/shop?signal=hormone",
  },
  {
    key: "gut",
    label: "Bloating or digestion",
    body: "Quiet gut support that pairs well with simple food changes.",
    href: "/shop?signal=gut",
  },
];

function PeopleLikeYouRail() {
  return (
    <section className="bg-white border-y border-emerald-100" data-testid="shop-people-like-you">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Where do I start?</p>
            <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light mt-1 leading-tight">
              People like you usually start here.
            </h2>
          </div>
          <Link to="/questionnaire" className="text-sm text-emerald-700 hover:text-emerald-800 inline-flex items-center" data-testid="shop-people-assessment-link">
            Not sure? Take the assessment <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {RAILS.map((r) => (
            <Link
              key={r.key}
              to={r.href}
              className="group rounded-2xl border border-emerald-100 bg-white p-5 hover:border-emerald-300 hover:shadow-sm transition"
              data-testid={`shop-people-rail-${r.key}`}
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-emerald-50 text-emerald-700 mb-3">
                <Compass className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <h3 className="font-display text-base text-emerald-950 font-light leading-snug">{r.label}</h3>
              <p className="text-[12.5px] text-emerald-900/70 mt-1.5 leading-relaxed">{r.body}</p>
              <span className="inline-block mt-3 text-[12px] text-emerald-700 group-hover:text-emerald-800 transition">
                Explore →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


// ============================================================================
//   "Need help choosing?" call-out
// ============================================================================
function NeedHelpChoosingBlock() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="rounded-3xl border border-amber-200/60 bg-gradient-to-br from-amber-50/70 via-white to-emerald-50/30 p-7 sm:p-12 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-center" data-testid="shop-need-help">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold">Need help choosing?</p>
          <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-1">
            Don't pick alone. We're built for moments like this.
          </h2>
          <p className="text-sm text-emerald-900/75 mt-3 max-w-xl leading-relaxed">
            Tell our wellness team a little about you and we'll match you to the right product within a few hours — at no cost.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3 sm:flex-col">
          <Link to="/questionnaire" data-testid="shop-need-help-assessment">
            <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-5">
              <Sparkles className="w-4 h-4 mr-1.5" /> Free assessment
            </Button>
          </Link>
          <Link to="/contact?intent=mentor" data-testid="shop-need-help-mentor">
            <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 px-5 py-5">
              <HeartHandshake className="w-4 h-4 mr-1.5" /> Book a mentor
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}


function EmptyState({ country }) {
  return (
    <div className="rounded-3xl border border-emerald-100 bg-emerald-50/30 p-10 sm:p-16 text-center" data-testid="shop-empty">
      <ShieldCheck className="w-10 h-10 mx-auto text-emerald-700/60 mb-3" strokeWidth={1.3} />
      <p className="font-display text-2xl text-emerald-950 font-light">
        We don't have products configured for {country} yet.
      </p>
      <p className="text-sm text-emerald-900/70 mt-2 max-w-md mx-auto leading-relaxed">
        Switch country in the top-right pill, or reach out to our team and we'll guide you in via WhatsApp.
      </p>
      <Link to="/contact" className="inline-flex items-center mt-5 text-emerald-700 hover:text-emerald-800">
        Contact us <ArrowRight className="w-4 h-4 ml-1.5" />
      </Link>
    </div>
  );
}
